import { useState } from 'react';
import { useBanks, useResolveAccount, useCreatePayoutDestination } from './hooks/usePayoutDestinations';
import type { CreatePayoutDestinationPayload, PayoutDestinationType } from './types';
import Button from '../../shared/components/Button';

interface Props {
  tenantId: string;
  /** Pre-fills the business name from the merchant record. */
  defaultBusinessName?: string;
  onClose: () => void;
}

export function RegisterPayoutDestinationModal({ tenantId, defaultBusinessName = '', onClose }: Props) {
  const { data: banks = [], isLoading: banksLoading } = useBanks();
  const resolve = useResolveAccount();
  const create = useCreatePayoutDestination(tenantId);

  const [businessName, setBusinessName] = useState(defaultBusinessName);
  const [businessEmail, setBusinessEmail] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [destinationType, setDestinationType] = useState<PayoutDestinationType>('LOCAL_BANK');
  const [commissionPercent, setCommissionPercent] = useState('');
  const [bvnVerified, setBvnVerified] = useState(false);
  const [error, setError] = useState('');

  const canResolve = bankCode && accountNumber.length >= 10;

  const handleResolve = async () => {
    setError('');
    const result = await resolve.mutateAsync({ accountNumber, bankCode });
    setAccountName(result.accountName);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!businessName.trim()) { setError('Business name is required'); return; }
    if (!bankCode) { setError('Select a bank'); return; }
    if (!accountNumber.trim()) { setError('Account number is required'); return; }

    let commissionRate: number | undefined;
    if (commissionPercent.trim()) {
      const pct = parseFloat(commissionPercent);
      if (isNaN(pct) || pct < 0 || pct > 100) { setError('Commission must be between 0 and 100%'); return; }
      commissionRate = pct / 100;
    }

    const payload: CreatePayoutDestinationPayload = {
      tenantId,
      businessName: businessName.trim(),
      businessEmail: businessEmail.trim() || undefined,
      bankCode,
      accountNumber: accountNumber.trim(),
      destinationType,
      commissionRate,
      bvnVerified,
    };
    await create.mutateAsync(payload);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 overflow-y-auto py-8">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-lg mx-4 p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">Register Payout Destination</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-muted-foreground text-2xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Business Name *</label>
            <input
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Bank *</label>
              <select
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                value={bankCode}
                onChange={(e) => { setBankCode(e.target.value); setAccountName(''); }}
                disabled={banksLoading}
              >
                <option value="">{banksLoading ? 'Loading banks…' : 'Select a bank'}</option>
                {banks.map((b) => (
                  <option key={b.code} value={b.code}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Destination Type</label>
              <select
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                value={destinationType}
                onChange={(e) => setDestinationType(e.target.value as PayoutDestinationType)}
              >
                <option value="LOCAL_BANK">Local Bank</option>
                <option value="REMITTANCE">Remittance</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Account Number *</label>
            <div className="flex gap-2">
              <input
                className="flex-1 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                value={accountNumber}
                onChange={(e) => { setAccountNumber(e.target.value); setAccountName(''); }}
                placeholder="10-digit NUBAN"
              />
              <button
                type="button"
                onClick={handleResolve}
                disabled={!canResolve || resolve.isPending}
                className="px-3 py-2 border border-border rounded-lg text-sm hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {resolve.isPending ? 'Resolving…' : 'Resolve'}
              </button>
            </div>
            {accountName && (
              <p className="text-sm text-green-700 mt-1">✓ {accountName}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Commission Rate (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                placeholder="Default (platform rate)"
                value={commissionPercent}
                onChange={(e) => setCommissionPercent(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Business Email</label>
              <input
                type="email"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                placeholder="Optional"
                value={businessEmail}
                onChange={(e) => setBusinessEmail(e.target.value)}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={bvnVerified} onChange={(e) => setBvnVerified(e.target.checked)} />
            BVN verified
          </label>

          <p className="text-xs text-muted-foreground">
            Registers split subaccounts on every enabled provider (Paystack / Flutterwave). Account-name resolution is best-effort.
          </p>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
              Cancel
            </button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Registering…' : 'Register'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
